import React from 'react';
import classNames from 'classnames/bind';

import { Stream } from 'types/stream';
import { MultiStreamWidget as MultiStreamWidgetType } from 'types/dashboard_widget';

import { UseStreamsParams, useStreams } from 'streams/hooks/useStreams';
import { useDashboardFilterParams } from 'dashboards/hooks/useDashboardFilterParams';
import { useMqttStreamsState } from 'streams/hooks/useMqttStreamsState';
import { useTagsFilterParams } from 'tags/hooks/useTagsFilterParams';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { QuerySearch } from 'components/QuerySearch/QuerySearch';
import { VideoSourceSnapshot } from 'components/VideoSourceSnapshot/VideoSourceSnapshot';
import { WebRTCPlayer } from 'components/WebRTCPlayer/WebRTCPlayer';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import styles from './MultiStreamWidget.module.scss';

const c = classNames.bind(styles);

export function MultiStreamWidget(widget: MultiStreamWidgetType) {
  const { options, autoPlay } = widget;

  const tagFilters = useTagsFilterParams();
  const { gateway_id } = useDashboardFilterParams();

  const [searchFilters, setSearchFilters] = React.useState<UseStreamsParams>(
    {}
  );

  const [queryFilters, setQueryFilters] = React.useState<UseStreamsParams>({});

  const searchQueryResult = useStreams({
    params: {
      ...options,
      ...searchFilters,
      ...tagFilters,
      gateway_ids: gateway_id ? [gateway_id] : undefined,
      stream_types: ['webrtc', 'rtsp'],
      limit: 20,
    },
    enabled: Object.keys(searchFilters).length > 0,
    keepPreviousData: false,
  });

  const { data: streams } = useStreams({
    params: {
      ...options,
      ...queryFilters,
      ...tagFilters,
      gateway_ids: gateway_id
        ? Array.from(
            new Set([
              gateway_id,
              ...(queryFilters.gateway_ids || []),
              ...(options?.gateway_ids || []),
            ])
          )
        : Array.from(
            new Set([
              ...(queryFilters.gateway_ids || []),
              ...(options?.gateway_ids || []),
            ])
          ),
      limit: 100,
      stream_types: ['webrtc', 'rtsp'],
    },
  });

  useMqttStreamsState(streams?.data ? streams.data.map(({ id }) => id) : []);

  // Filter streams to only show one stream per camera
  const filteredStreams = streams?.data?.filter((stream, _, array) => {
    if (!stream.camera_id) {
      return true;
    }

    const cameraStreams = array.filter(
      (selectedStream) => selectedStream.camera_id === stream.camera_id
    );

    const isH264 = (stream: Stream) =>
      stream.name.toLowerCase().includes('h264') ||
      stream.name.toLowerCase().includes('h.264') ||
      (stream.source === 'pipeline_stream' && stream.stream_type === 'webrtc');

    const h264Streams = cameraStreams.filter(isH264);

    if (h264Streams.length > 0) {
      return stream.id === h264Streams[0].id;
    }

    return stream.id === cameraStreams[0].id;
  });

  const handleSearchInput = React.useCallback((value?: string) => {
    if (!value) {
      setSearchFilters({});
      setQueryFilters({});
      return;
    }

    setSearchFilters((previousFilters) => ({
      ...previousFilters,
      stream_names: [value],
    }));
  }, []);

  function handleApplyFilters() {
    setQueryFilters(searchFilters);
  }

  const hasMatchingStreams = filteredStreams && Boolean(filteredStreams.length);

  return (
    <Widget widget={widget}>
      {!hasMatchingStreams && (
        <EmptyView>No streams matching the filter.</EmptyView>
      )}

      {hasMatchingStreams && (
        <div className={c('wrap')}>
          <div className={c('header')}>
            {!autoPlay && (
              <QuerySearch
                className={c('search')}
                entityName="stream"
                searchLabel="Search streams..."
                searchResultField="name"
                searchQueryResult={searchQueryResult}
                onApplyFilters={handleApplyFilters}
                onValueChange={handleSearchInput}
                totalElements={filteredStreams?.length}
              />
            )}
          </div>

          <div className={c('grid')}>
            {filteredStreams.map((stream) => {
              if (!stream.id) return null;

              return (
                <div className={c('grid-item')} key={stream.id}>
                  {autoPlay ? (
                    <WebRTCPlayer
                      className={c('stream-player')}
                      streamID={stream.id}
                      auto
                    />
                  ) : (
                    <VideoSourceSnapshot
                      className={c('grid-item-preview')}
                      source={stream}
                      autoRefresh
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Widget>
  );
}
